/**
 * Screens 16 «Выходные и отпуск» and 16a «Период» (salon-mockup.html timeoff /
 * timeoffedit; D-58). Planned periods as cards; «+», «Добавить период» and
 * every card open sheet 16a. A period has no PATCH (doc 04 §6), so saving an
 * edited one adds the new period and then deletes the old.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { useDuration } from '../components/Agenda';
import { AppBar, Sheet, Shell } from '../components/Shell';
import { Wheel } from '../components/Wheel';
import { Icon } from '../components/icons';
import { ApiError, api } from '../core/api';
import { errorText } from '../i18n';
import { addDays, hm, instant, ymd } from '../lib/time';
import type { TimeOff } from '../types';

const dmy = (day: string) => day.split('-').reverse().join('.');
const DAY_MS = 86_400_000;

/** Whole days when it runs midnight to midnight (Bishkek), else a time range. */
export function periodShape(p: Pick<TimeOff, 'starts_at' | 'ends_at'>) {
  const whole = hm(p.starts_at) === '00:00' && hm(p.ends_at) === '00:00';
  const from = ymd(p.starts_at);
  const to = whole ? addDays(ymd(p.ends_at), -1) : ymd(p.ends_at);
  const days = Math.round((Date.parse(p.ends_at) - Date.parse(p.starts_at)) / DAY_MS);
  const minutes = Math.round((Date.parse(p.ends_at) - Date.parse(p.starts_at)) / 60_000);
  return { whole, from, to, days, minutes };
}

export function useTimeOff() {
  // «Запланировано»: everything not yet over.
  return useQuery({ queryKey: ['time-off'], queryFn: () => api.timeOff(new Date().toISOString()) });
}

export function TimeOffScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const periods = useTimeOff();
  const duration = useDuration();
  const open = (to: string) => navigate(`/settings/time-off/${to}`);
  const editing = id === 'new' ? null : periods.data?.find((p) => p.id === id);

  return (
    <Shell active="settings">
      <AppBar
        title={t('timeoff.title')}
        back={() => navigate('/settings')}
        right={
          <button type="button" className="iconbtn" aria-label={t('timeoff.add')} onClick={() => open('new')}>
            <Icon.plus />
          </button>
        }
      />
      <div className="content">
        <div className="pane">
          <span className="cap">{t('timeoff.planned')}</span>
          {periods.data?.map((p) => {
            const s = periodShape(p);
            const when = s.whole
              ? s.days === 1
                ? `${dmy(s.from)} · ${t('timeoff.allDay')}`
                : `${dmy(s.from)} — ${dmy(s.to)}`
              : s.from === s.to
                ? `${dmy(s.from)} · ${hm(p.starts_at)} — ${hm(p.ends_at)}`
                : `${dmy(s.from)} ${hm(p.starts_at)} — ${dmy(s.to)} ${hm(p.ends_at)}`;
            return (
              <div key={p.id} className="card" data-id={p.id} role="button" tabIndex={0} onClick={() => open(p.id)} style={{ cursor: 'pointer' }}>
                <div className="row">
                  <span className="grow">
                    <b>{p.reason || (s.whole ? t('timeoff.dayOff') : t('timeoff.break'))}</b>
                    <span className="sm tnum" style={{ display: 'block' }}>
                      {when}
                    </span>
                  </span>
                  <span className="pill">{s.whole ? t('timeoff.days', { count: s.days }) : duration(s.minutes)}</span>
                </div>
              </div>
            );
          })}
          <div className="warnbox">{t('timeoff.warn')}</div>
          <button type="button" className="btn blk" onClick={() => open('new')}>
            {t('timeoff.addPeriod')}
          </button>
        </div>
      </div>
      {id && (id === 'new' || editing) && <PeriodSheet key={id} period={editing ?? null} onClose={() => navigate('/settings/time-off')} />}
    </Shell>
  );
}

function PeriodSheet({ period, onClose }: { period: TimeOff | null; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const shape = period ? periodShape(period) : null;
  const today = ymd(new Date());
  const [reason, setReason] = useState(period?.reason ?? '');
  const [from, setFrom] = useState(shape?.from ?? today);
  const [to, setTo] = useState(shape?.to ?? today);
  const [whole, setWhole] = useState(shape?.whole ?? true);
  const [starts, setStarts] = useState(period && !shape?.whole ? hm(period.starts_at) : '09:00');
  const [ends, setEnds] = useState(period && !shape?.whole ? hm(period.ends_at) : '18:00');
  const [editing, setEditing] = useState<'starts' | 'ends'>('ends');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    // A period hatches the calendar and cuts free gaps (D-41, D-37).
    for (const key of ['time-off', 'today', 'day', 'week', 'month']) void qc.invalidateQueries({ queryKey: [key] });
  };
  const fail = (err: unknown) => setError(errorText(err instanceof ApiError ? err.code : 'fallback'));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const startsAt = whole ? instant(from, '00:00') : instant(from, starts);
    const endsAt = whole ? instant(addDays(to, 1), '00:00') : instant(to, ends);
    if (Date.parse(endsAt) <= Date.parse(startsAt)) return setError(t('timeoff.order'));
    setBusy(true);
    try {
      await api.createTimeOff({ starts_at: startsAt, ends_at: endsAt, reason: reason.trim() || null });
      if (period) await api.deleteTimeOff(period.id);
      refresh();
      onClose();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!period) return;
    setBusy(true);
    try {
      await api.deleteTimeOff(period.id);
      refresh();
      onClose();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const dateField = (label: string, value: string, set: (v: string) => void) => (
    <div className="fld grow">
      <label>{label}</label>
      {/* Shown as the mockup writes it, «30.09.2026»; the browser's own picker sits invisibly over it. */}
      <div className="inp tnum" style={{ position: 'relative' }}>
        <span>{dmy(value)}</span>
        <input aria-label={label} className="picker" type="date" value={value} onChange={(e) => e.target.value && set(e.target.value)} style={{ left: 0, width: '100%' }} />
      </div>
    </div>
  );
  const timeField = (which: 'starts' | 'ends', label: string, value: string) => (
    <div className="fld grow">
      <label>{label}</label>
      <div className={`inp tnum${editing === which ? ' focus' : ''}`} role="button" tabIndex={0} aria-label={label} onClick={() => setEditing(which)}>
        {value}
      </div>
    </div>
  );

  return (
    <Sheet onClose={onClose} className="narrow">
      <form onSubmit={submit} noValidate>
        <h3>{period ? period.reason || (shape?.whole ? t('timeoff.dayOff') : t('timeoff.break')) : t('timeoff.new')}</h3>
        {error && <div className="warnbox form-error" role="alert">{error}</div>}
        <div className="fld">
          <label>
            {t('timeoff.reason')}
            <input className="inp" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} placeholder={t('book.optional')} />
          </label>
        </div>
        <div className="row" style={{ gap: 11, alignItems: 'flex-start' }}>
          {dateField(t('timeoff.from'), from, (v) => {
            setFrom(v);
            if (to < v) setTo(v);
          })}
          {dateField(t('timeoff.to'), to, setTo)}
        </div>
        <div className="seclist">
          <div className="si">
            <span className="grow">
              <b style={{ fontSize: 14 }}>{t('timeoff.wholeDay')}</b>
            </span>
            <button type="button" role="switch" aria-checked={whole} aria-label={t('timeoff.wholeDay')} className={`sw-t${whole ? '' : ' off'}`} style={{ border: 0, padding: 0, cursor: 'pointer' }} onClick={() => setWhole(!whole)} />
          </div>
        </div>
        {!whole && (
          <>
            <div className="row" style={{ gap: 11, alignItems: 'flex-start' }}>
              {timeField('starts', t('schedule.opens'), starts)}
              {timeField('ends', t('schedule.closes'), ends)}
            </div>
            <Wheel
              label={editing === 'starts' ? t('schedule.opens') : t('schedule.closes')}
              value={editing === 'starts' ? starts : ends}
              onChange={(v) => (editing === 'starts' ? setStarts(v) : setEnds(v))}
            />
          </>
        )}
        <div className="btnrow">
          <button type="button" className="btn" onClick={onClose}>
            {t('services.cancel')}
          </button>
          <button type="submit" className="btn pri" disabled={busy}>
            {t('services.save')}
          </button>
        </div>
        {period && (
          <button type="button" className="btn blk" disabled={busy} onClick={() => void remove()} style={{ marginTop: 9, color: 'var(--st-overlap-fg)', borderColor: 'var(--st-overlap-bd)' }}>
            {t('timeoff.delete')}
          </button>
        )}
      </form>
    </Sheet>
  );
}
